import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-interactive-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './interactive-map.html',
  styleUrl: './interactive-map.scss'
})
export class InteractiveMap {
  s3BaseUrl = environment.s3BaseUrl;
  // Estado del mapa
  activeMarker: string | null = null;
  zoomLevel = 1;
  panX = 0;
  panY = 0;

  // Límites de zoom
  minZoom = 0.5;
  maxZoom = 3;

  // Marcadores del mapa
  markers = [
    {
      id: 'entrada',
      name: 'Entrada Principal',
      description: 'Acceso principal al resort desde el estacionamiento',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/entrada.png`,
      left: '10%',
      top: '50%'
    },
    {
      id: 'megapiscina',
      name: 'MegaPiscina',
      description: 'La piscina más grande del complejo',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/mega_piscina.png`,
      left: '25%',
      top: '20%'
    },
    {
      id: 'toboganes',
      name: 'Toboganes',
      description: 'Atracciones acuáticas y toboganes',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/toboganes1.png`,
      left: '32%',
      top: '45%'
    },
    {
      id: 'piletas',
      name: 'Piletas',
      description: 'Piletas para niños y adultos',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/piletas.png`,
      left: '45.2%',
      top: '53%'
    },
    {
      id: 'playa-artificial',
      name: 'Playa Artificial',
      description: 'Bungalows sobre el agua y playa serpenteante',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/playa_artificial.png`,
      left: '65.7%',
      top: '45%'
    },
    {
      id: 'toboganes-secundarios',
      name: 'Toboganes',
      description: 'Atracciones acuáticas y toboganes',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/toboganes2.png`,
      left: '50%',
      top: '30%'
    },
    {
      id: 'bungalows',
      name: 'Bungalows',
      description: 'Bungalows privados y playa serpenteante',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/bungalows.png`,
      left: '89%',
      top: '69%'
    },
    {
      id: 'areas-verdes',
      name: 'Áreas Verdes',
      description: 'Extensas zonas de césped y vegetación',
      icon: `${environment.s3BaseUrl}/icons/features/location_map.png`,
      image: `${environment.s3BaseUrl}/images/sections/interactive_map/area_verde.png`,
      left: '56%',
      top: '23%'
    }
  ];

  // Seleccionar marcador
  selectMarker(markerId: string): void {
    this.activeMarker = this.activeMarker === markerId ? null : markerId;
  }

  // Controles de zoom
  zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel += 0.2;
      this.zoomLevel = Math.min(this.zoomLevel, this.maxZoom);
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel -= 0.2;
      this.zoomLevel = Math.max(this.zoomLevel, this.minZoom);
      
      // Reset pan cuando hacemos zoom out completo
      if (this.zoomLevel === this.minZoom) {
        this.panX = 0;
        this.panY = 0;
      }
    }
  }

  // Reset del mapa
  resetMap(): void {
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;
    this.activeMarker = null;
  }

  // Obtener el marcador activo
  getActiveMarker() {
    return this.markers.find(marker => marker.id === this.activeMarker);
  }

  // Obtener la posición del marcador activo
  getActiveMarkerPosition() {
    const activeMarker = this.getActiveMarker();
    if (activeMarker) {
      return {
        left: activeMarker.left,
        top: activeMarker.top
      };
    }
    return null;
  }

  // Obtener la posición inteligente del tooltip
  getSmartTooltipPosition() {
    const activeMarker = this.getActiveMarker();
    if (!activeMarker) return null;

    // Convertir porcentajes a píxeles para cálculos
    const mapWidth = 1000; // Ancho del mapa en píxeles
    const mapHeight = 625; // Alto del mapa en píxeles (16:10 ratio)
    
    const markerLeft = (parseFloat(activeMarker.left) / 100) * mapWidth;
    const markerTop = (parseFloat(activeMarker.top) / 100) * mapHeight;
    
    // Dimensiones estimadas del tooltip
    const tooltipWidth = 300; // Ancho estimado del tooltip
    const tooltipHeight = 250; // Alto estimado del tooltip
    
    let position = {
      left: activeMarker.left,
      top: activeMarker.top,
      transform: 'translate(-50%, -100%)',
      marginTop: '-28px',
      arrowDirection: 'down'
    };

    // Verificar si hay espacio arriba
    if (markerTop < tooltipHeight + 50) {
      // No hay espacio arriba, mostrar abajo
      position.transform = 'translate(-50%, 0%)';
      position.marginTop = '28px';
      position.arrowDirection = 'up';
    }

    // Verificar si hay espacio a la derecha
    if (markerLeft + (tooltipWidth / 2) > mapWidth - 50) {
      // No hay espacio a la derecha, mostrar a la izquierda del marcador
      position.left = activeMarker.left;
      position.transform = 'translate(-110%, -35%)';
      position.arrowDirection = 'right';
      if (markerTop < tooltipHeight + 50) {
        position.transform = 'translate(-110%, 0%)';
        position.arrowDirection = 'right';
      }
    }

    // Verificar si hay espacio a la izquierda
    if (markerLeft - (tooltipWidth / 2) < 50) {
      // No hay espacio a la izquierda, mostrar a la derecha del marcador
      position.left = activeMarker.left;
      position.transform = 'translate(10%, -35%)';
      position.arrowDirection = 'left';
      if (markerTop < tooltipHeight + 50) {
        position.transform = 'translate(10%, 0%)';
        position.arrowDirection = 'left';
      }
    }

    return position;
  }

  getImageUrl(path: string): string {
    return `${this.s3BaseUrl}/${path}`;
  }
}
